import { useEffect, useState } from "react";
import { Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import Swal from "sweetalert2";
import collaborationApi from "../../../api/collaborationApi";

const CollabCredits = ({ collabId, collaborationName }) => {
  const [collabData, setCollabData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [extraDays, setExtraDays] = useState(0);
  const [extraCharge, setExtraCharge] = useState(0);
  const [newDeadline, setNewDeadline] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [payloadPreview, setPayloadPreview] = useState(null); // ADDED
  const user = JSON.parse(localStorage.getItem("user"));
  const senderEmail = user?.userEmail;
  const referralCode = user?.referralCode;

  useEffect(() => {
    if (!collabId) return;
    collaborationApi
      .getCollaBriefById(collabId)
      .then((res) => setCollabData(res.data))
      .catch((err) => console.error(err));
  }, [collabId]);

  if (!collabData) return <div>Loading...</div>;

  const handleRequest = () => {
    setShowModal(true);
    setExtraDays(0);
    setExtraCharge(0);
    const today = new Date();
    setNewDeadline(today.toISOString().split("T")[0]);
    setMobileNumber("");
  };

  const handleDaysChange = (e) => {
    const days = Number(e.target.value);
    setExtraDays(days);

    // 2 Paid Frymes for every extra day
    const paidFrymes = days * 2;
    setExtraCharge(paidFrymes);

    // New deadline = today + (extra days + 1)
    const date = new Date();
    if (days > 0) {
      date.setDate(date.getDate() + days + 1);
    }
    setNewDeadline(date.toISOString().split("T")[0]);
  };

  const submitRequest = () => {
    if (!mobileNumber) {
      Swal.fire({
        icon: "warning",
        title: "Mobile Number Required",
        text: "Please enter your mobile number.",
      });
      return;
    }

    if (!collabId) {
      Swal.fire({
        icon: "error",
        title: "Missing Collaboration ID",
        text: "collabId is not set — cannot submit this request.",
      });
      return;
    }

    setIsSubmitting(true);

    const payload = {
      extraTime: extraDays,
      extraCharge: extraCharge,
      newDeadline: newDeadline,
      mobileNumber: mobileNumber,
      referralCode: referralCode,
      userEmail: senderEmail,
    };

    // Full preview: includes collabId + the endpoint being called, not just the body
    setPayloadPreview({
      endpoint: `PUT /updateCollaboration?collabId=${collabId}`,
      collabId: collabId,
      body: payload,
    });

    collaborationApi
      .updateRequestCredits(collabId, payload)
      .then((res) => {
        // capture the actual response too, so you can see what came back
        setPayloadPreview((prev) => ({
          ...prev,
          response: res.data,
        }));

        Swal.fire({
          icon: "success",
          title: "Request Submitted Successfully!",
          text: "Please wait up to 24 hours. Your franchise representative will contact you and confirm your Frymes request.",
          confirmButtonColor: "#3085d6",
        });

        setShowModal(false);
        setExtraDays(0);
        setExtraCharge(0);
        setNewDeadline("");
        setMobileNumber("");
        setIsSubmitting(false);
      })
      .catch((err) => {
        console.error("API ERROR:", err.response?.data || err.message);
        setIsSubmitting(false);

        // capture the error response for preview too
        setPayloadPreview((prev) => ({
          ...prev,
          error: {
            status: err.response?.status,
            data: err.response?.data,
            message: err.message,
          },
        }));

        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text:
            err.response?.data?.message ||
            err.response?.data ||
            "Something went wrong. Please try again.",
        });
      });
  };

  const isActive = collabData.status === true;

  return (
    <>
      <br />

      <h4 className="mb-2">
        <strong>Frymes of {collaborationName}</strong>
        <Col className="text-end">
          <Button onClick={handleRequest}>Request Extension</Button>
        </Col>
      </h4>

      <Row>
        <Col md={4}>
          <Card className="p-3">
            <h5>
              <strong>Base Plan</strong>
            </h5>

            <p>
              Base Frymes: <b>10</b>
            </p>

            <p>
              Duration: <b>5 Days</b>
            </p>

            <p>
              Balance Free Frymes: <b>{collabData.collabCredits || 0}</b>
            </p>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="p-3">
            <h5>
              <strong>Premium Details</strong>
            </h5>

            <p>
              Paid Frymes: <b>{collabData.extraCharge || 0}</b>
            </p>

            <p>
              Extension Days: <b>{collabData.extraTime || 0}</b>
            </p>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="p-3">
            <h5>
              <strong>Status</strong>
            </h5>

            <p>
              Current Status{" "}
              <b style={{ color: isActive ? "green" : "red" }}>
                {isActive ? "Active" : "Inactive"}
              </b>
            </p>

            <p>
              Actions: {isActive ? "Allowed (Upload, Assign Tasks)" : "Blocked"}
            </p>
          </Card>
        </Col>
      </Row>

      <Card className="mt-3 p-3 bg-light">
        <b>Important Rules</b>

        <ul className="mt-2">
          <li>Each collaboration starts with 10 frymes.</li>
          <li>2 frymes are deducted daily automatically.</li>
          <li>Default active period is 5 days.</li>
          <li>After expiry, collaboration becomes inactive.</li>
          <li>Inactive collaborations cannot upload or assign tasks.</li>
        </ul>
      </Card>

      {/* Debug preview — stays visible even after the modal closes on success */}
      {payloadPreview && (
        <Card className="mt-3 p-3">
          <strong>Debug — Last Request Preview</strong>
          <pre
            style={{
              background: "#f5f5f5",
              padding: "10px",
              borderRadius: "4px",
              fontSize: "12px",
              whiteSpace: "pre-wrap",
              maxHeight: "300px",
              overflowY: "auto",
              marginTop: "8px",
            }}
          >
            {JSON.stringify(payloadPreview, null, 2)}
          </pre>
        </Card>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Request Extension</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>
            <strong>collabId:</strong> {collabId ?? "undefined"}
          </p>

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Extra Days</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={extraDays}
                onChange={handleDaysChange}
                placeholder="Enter Extra Days"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Paid Frymes</Form.Label>
              <Form.Control type="number" value={extraCharge} readOnly />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Mobile Number</Form.Label>
              <Form.Control
                type="tel"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                placeholder="Enter Mobile Number"
                maxLength={10}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>New Deadline</Form.Label>
              <Form.Control
                type="text"
                value={newDeadline || collabData.deadline}
                readOnly
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>

          <Button
            variant="primary"
            onClick={submitRequest}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Submitting...
              </>
            ) : (
              "Submit Request"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CollabCredits;